import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { AuthService } from "@/services";
import { Button } from "@/components/ui/button";
import { Logo } from "@/components/core/logo";
import { useAuth } from "@/contexts";
import { showToast } from "@/hooks/useToast";

export const Route = createFileRoute("/auth/verify-email")({
  validateSearch: (search: Record<string, unknown>) => {
    return {
      token: (search.token as string) || "",
    };
  },
  component: RouteComponent,
});

function RouteComponent() {
  const navigate = useNavigate();
  const { token } = Route.useSearch();
  const [status, setStatus] = useState<"loading" | "success" | "error">("loading");
  const { saveAuth } = useAuth();

  useEffect(() => {
    if (!token) {
      setStatus("error");
      return;
    }

    AuthService.verifyEmail(token)
      .then(async () => {
        setStatus("success");

        // Attempt to finalize login if we have pending info in sessionStorage
        try {
          const pending = sessionStorage.getItem("pending_verify_email");
          if (pending) {
            const parsed = JSON.parse(pending) as { email?: string; telefone?: string };
            const res = await AuthService.finalizeLogin({ email: parsed.email, telefone: parsed.telefone });
            if (res?.access) {
              saveAuth(res as any);
              sessionStorage.removeItem("pending_verify_email");
              showToast.success("Login realizado com sucesso!");
              navigate({ to: "/app" });
              return;
            }
          }
        } catch (err) {
          console.error("Failed to finalize login after email verification:", err);
        }
      })
      .catch(() => setStatus("error"));
  }, []);

  return (
    <div
      className="min-h-screen flex items-center justify-center p-4 relative"
      style={{
        backgroundImage: `url("/authentication-bg.svg")`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
      }}
    >
      <div className="w-full max-w-md relative z-10">
        <div className="bg-white rounded-4xl shadow-xl p-8 text-center">
          <div className="flex items-center justify-center mb-6">
            <Logo className="h-12" isTraditionalLogo />
          </div>

          {status === "loading" && (
            <div className="py-8">
              <div className="w-12 h-12 border-4 border-gray-200 border-t-gray-800 rounded-full animate-spin mx-auto mb-4" />
              <p className="text-gray-600">Verificando seu e-mail...</p>
            </div>
          )}

          {status === "success" && (
            <div className="py-4">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg
                  className="w-8 h-8 text-green-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M5 13l4 4L19 7"
                  />
                </svg>
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">
                E-mail verificado com sucesso!
              </h2>
              <p className="text-gray-600 mb-6">Sua conta está ativa e pronta para uso.</p>
              <Button className="w-full" onClick={() => navigate({ to: "/auth/identify" })}>
                Fazer login
              </Button>
            </div>
          )}

          {status === "error" && (
            <div className="py-4">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg
                  className="w-8 h-8 text-red-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Link inválido ou expirado</h2>
              <p className="text-gray-600 mb-6">
                O link de verificação é inválido ou já expirou. Tente fazer o cadastro novamente.
              </p>
              <Button
                variant="outline"
                className="w-full"
                onClick={() => navigate({ to: "/auth/identify" })}
              >
                Voltar
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
